/**
 * Composant de protection des routes basé sur les permissions
 */
import React from 'react';
import { permissionService, PERMISSIONS } from '../../services/permissions';

interface PermissionGuardProps {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  role?: string;
  roles?: string[];
  requireAnyRole?: boolean;
  fallback?: React.ReactNode;
  showError?: boolean;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  permissions,
  requireAll = false,
  role,
  roles,
  requireAnyRole = false,
  fallback = null,
  showError = false
}) => {
  // Vérifier les permissions
  let hasPermission = true;
  
  if (permission) {
    hasPermission = permissionService.hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    if (requireAll) {
      hasPermission = permissionService.hasAllPermissions(permissions);
    } else {
      hasPermission = permissionService.hasAnyPermission(permissions);
    }
  }
  
  // Vérifier les rôles
  let hasRole = true;
  
  if (role) {
    hasRole = permissionService.hasRole(role);
  } else if (roles && roles.length > 0) {
    if (requireAnyRole) {
      hasRole = permissionService.hasAnyRole(roles);
    } else {
      hasRole = permissionService.hasAllPermissions(roles);
    }
  }
  
  const isAuthorized = hasPermission && hasRole;
  
  if (!isAuthorized) {
    if (showError) {
      return (
        <div className="permission-error">
          <div className="alert alert-danger">
            <h4>Accès refusé</h4>
            <p>Vous n'avez pas les permissions nécessaires pour accéder à cette ressource.</p>
          </div>
        </div>
      );
    }
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
};

// Composants de commodité pour des cas d'usage courants
export const RequirePermission: React.FC<{
  permission: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permission, children, fallback = null }) => (
  <PermissionGuard permission={permission} fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const RequireAnyPermission: React.FC<{
  permissions: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permissions, children, fallback = null }) => (
  <PermissionGuard permissions={permissions} fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const RequireAllPermissions: React.FC<{
  permissions: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ permissions, children, fallback = null }) => (
  <PermissionGuard permissions={permissions} requireAll={true} fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const RequireRole: React.FC<{
  role: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ role, children, fallback = null }) => (
  <PermissionGuard role={role} fallback={fallback}>
    {children}
  </PermissionGuard>
);

export const RequireAnyRole: React.FC<{
  roles: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ roles, children, fallback = null }) => (
  <PermissionGuard roles={roles} requireAnyRole={true} fallback={fallback}>
    {children}
  </PermissionGuard>
);

// Hook pour vérifier les permissions dans les composants
export const usePermissions = () => {
  const hasPermission = (permission: string): boolean => {
    return permissionService.hasPermission(permission);
  };
  
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissionService.hasAnyPermission(permissions);
  };
  
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissionService.hasAllPermissions(permissions);
  };
  
  const hasRole = (role: string): boolean => {
    return permissionService.hasRole(role);
  };
  
  const hasAnyRole = (roles: string[]): boolean => {
    return permissionService.hasAnyRole(roles);
  };
  
  const canPerformAction = (action: string, resource: string): boolean => {
    return permissionService.canPerformAction(action, resource);
  };
  
  const getUserPermissions = () => {
    return permissionService.getUserPermissions();
  };
  
  const getUserRoles = () => {
    return permissionService.getCurrentUserRoles();
  };
  
  const getUserPermissionCodes = () => {
    return permissionService.getUserPermissionCodes();
  };
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    canPerformAction,
    getUserPermissions,
    getUserRoles,
    getUserPermissionCodes,
    PERMISSIONS
  };
};

export default PermissionGuard;
